import React, { useRef, useState, useImperativeHandle } from "react";
import { Dialog, DialogContent, DialogActions, Grid } from "@mui/material";
import { v4 as uuid } from "uuid";
import _ from "lodash";
import { PEOPLE_TYPE } from "@icoach/constants/";
import CheckinMultipleDateTimeBox from "../checkin/CheckinMultipleDateTimeBox";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import Tablenization from "@icoach/components/Tablenization";
import { Stack, Button, AutocompleteField, Switch, FormControlLabel } from "@curves/ui-base";
import { refIsRequiredError } from "@util/utils";
import { parseDate, getNowTime } from "@util/moment";
import usePeopleApi from "@apis/usePeopleApi";
import useMemberCheckInApi from "@apis/useMemberCheckInApi";
import useSwitch from "@util/hook/useSwitch";
import clsx from "clsx";
import useOptions from '@util/hook/useOptions';

const CancelAddDialog = React.forwardRef((props, ref) => {
    const { onClear: onClearProps, onClose: onCloseProps } = props;
    const [open, setOpen] = useState(false);

    const handleConfirm = () => {
        onClearProps && onClearProps();
        setOpen(false);
    };

    const handleOnClose = () => {
        onCloseProps && onCloseProps();
        setOpen(false);
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                onClose: () => handleOnClose(),
                onOpen: () => setOpen(true),
            };
        },
        // eslint-disable-next-line
        [],
    );

    return (
        <Dialog open={open}>
            <GradientColorHeader onClose={handleOnClose}>訊息</GradientColorHeader>
            <DialogContent className="mt-3">您確定要放棄新增手動簽到並清空？</DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={handleConfirm}>
                    確定
                </Button>
            </DialogActions>
        </Dialog>
    );
});

const rowLabel = [
    {
        cellKey: "",
        headerLabel: ({ count }) => `項次(${count})`,
        align: "center",
        width: 5,
        formatCell: (_value, _rowData, { sequence }) => sequence,
    },
    {
        cellKey: "dateTime",
        headerLabel: "簽到時間",
        width: 16,
        align: "center",
        formatCell: _value => parseDate(_value, "YYYY-MM-DD HH:mm", false),
    },
    {
        cellKey: "date",
        headerLabel: "",
        align: "right",
        formatCell: (_value, _rowData, { onDelete }) => {
            return <Button onClick={() => onDelete(_value, "date")}>刪除</Button>;
        },
    },
];

const CheckinDialog = React.forwardRef((props, ref) => {
    const { onClose: onCloseProps, open } = props;
    const { getPeopleApi } = usePeopleApi();
    const { getMemberCheckinOptionsApi, postMemberCheckinApi } = useMemberCheckInApi();
    const { options: checkinOptions } = useOptions({
        getOptionsApi: getMemberCheckinOptionsApi,
        isFetchOnMount: true
    })
    const [peopleID, setPeopleID] = useState({
        id: null,
        next: null,
    });
    const [crossClubID, setCrossClubID] = useState(0);
    const [rowsData, setRowsData] = useState([]);
    const [options, setOptions] = useState([]);
    const { isOn, handleToggle } = useSwitch(false);

    const multipleDateTimeRef = useRef(null);
    const peopleRef = useRef(null);
    const cancelAddDialogRef = useRef(null);
    const crossClubRef = useRef(null);

    // 額外訊息視窗 不改變原值
    const handleDialogMessageClose = () => {
        const newObj = { id: peopleID.id, next: null };
        setPeopleID(newObj);
    };

    // 額外訊息視窗 確認關閉要清空
    const handleClearRowData = () => {
        setRowsData([]);
        setPeopleID(pre => ({ id: pre.next, next: null }));
    };

    // 額外訊息視窗 判斷是否改變input
    const handleMemberChange = peopleData => {
        const pID = peopleData?.id ?? null;
        if (!_.isEmpty(rowsData) && peopleID.id && peopleID !== pID) {
            cancelAddDialogRef.current.onOpen();
            setPeopleID(pre => ({ ...pre, next: pID }));
        } else {
            setPeopleID(pre => ({ ...pre, id: pID }));
        }
    };

    const handleCrossClubChange = crossClubData => {
        const crossClubID = crossClubData?.value ?? 0;
        if (isOn && crossClubID !== 0) {
            setCrossClubID(crossClubID);
        } else {
            setCrossClubID(0);
        }
    }

    // 關閉本身視窗 按鈕
    const handleOnClose = e => {
        setRowsData([]);
        setPeopleID({
            id: null,
            next: null,
        });
        onCloseProps && onCloseProps(null);
    };

    // 確認送出 按鈕
    const handleConfirmOnClick = e => {
        
        const assignCrossClubID = isOn ? crossClubID : 0;
        
        const params = rowsData.map(({ id: memberID, dateTime: checkinDate }) => ({
            memberID,
            checkinDate,
            crossClubID : assignCrossClubID
        }));

        if (params.length === 0) {
            refIsRequiredError(peopleRef, multipleDateTimeRef);
            return;
        }

        if (isOn && assignCrossClubID === 0) {
            refIsRequiredError(crossClubRef);
            return;
        }

        postCheckinApi(params);
    };

    // one box 回傳陣列 [date...],簽到 按鈕
    const handleSigninClick = e => {
        if (!refIsRequiredError(peopleRef, multipleDateTimeRef)) {
            const hms = parseDate(getNowTime(), "HH:mm:ss");
            const memberInfo = peopleRef.current.getResult();
            const dateArrays = multipleDateTimeRef.current.getResult();
            const target = dateArrays.map(target => {
                return { dateTime: `${target}T${hms}`, date: target, time: hms, ...memberInfo };
            });
            let newTarget = [...rowsData, ...target];
            newTarget = _.uniqBy(newTarget, item => item.date);
            newTarget = _.orderBy(newTarget, item => item.date);

            if (!isOn || !refIsRequiredError(crossClubRef)) {
                setRowsData(newTarget);
            }
        }

    };

    const handleOnDelete = (_id, mapKey) => {
        setRowsData(pre => {
            if (!Array.isArray(pre)) return [];
            else
                return pre.reduce((acc, cur) => {
                    let targetAry = [...acc];
                    if (cur[mapKey] !== _id) {
                        targetAry.push(cur);
                    }
                    return targetAry;
                }, []);
        });
    };

    const doMemberListApi = async () => {
        const resp = await getPeopleApi(PEOPLE_TYPE.ALLMEMBER);
        if (resp) setOptions(resp);
    };

    const postCheckinApi = async params => {
        const resp = await postMemberCheckinApi(params);
        if (resp) {
            handleOnClose();
        }
    };

    React.useLayoutEffect(
        () => {
            doMemberListApi();
        },
        // eslint-disable-next-line
        [],
    );

    return (
        <React.Fragment>
            <Dialog open={open} PaperProps={{ sx: { maxWidth: 1020, width: 980 } }} fullWidth>
                <GradientColorHeader onClose={handleOnClose}>手動簽到</GradientColorHeader>
                <DialogContent className="checkin-dialog mt-3">
                    <Grid container spacing={2} columns={10}>
                        <Grid item xs={4}>
                            <AutocompleteField
                                key={uuid()}
                                ref={peopleRef}
                                defaultValue={peopleID?.id ?? null}
                                onChange={handleMemberChange}
                                className="mb-3"
                                options={options}
                                optionKeys={["name", "nickName", "mobile", "no"]}
                                optionMaskKeys={["maskName", "nickName", "maskMobile", "no"]}
                                primaryKey={"id"}
                                inputProps={{
                                    placeholder: `請輸入姓名/手機/會員編碼`,
                                }}
                                fullWidth
                                required
                            />
                            <Tablenization height={360}>
                                <Tablenization.TableRowHeader headerRow={rowLabel} className="checkin-table-header" count={rowsData.length} />
                                <Tablenization.TableRowBody headerRow={rowLabel} rowsData={rowsData} onDelete={handleOnDelete} />
                            </Tablenization>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack className="mb-3" spacing={2}>
                                <FormControlLabel className={"m-0"} control={<Switch defaultChecked={isOn} onChange={handleToggle} />} prefixLabel={"跨店運動"}
                                                  labelPlacement="start" />
                                <AutocompleteField
                                    key={uuid()}
                                    ref={crossClubRef}
                                    className={clsx({ "visibility-hidden": !isOn })}
                                    defaultValue={crossClubID}
                                    onChange={handleCrossClubChange}
                                    options={checkinOptions?.clubOptions ?? []}
                                    optionKeys={["text"]}
                                    primaryKey={'value'}
                                    inputProps={{
                                        placeholder: `請輸入店舖名稱`,
                                    }}
                                    required
                                />
                            </Stack>
                            <CheckinMultipleDateTimeBox className="mb-4" ref={multipleDateTimeRef} onSigninClick={handleSigninClick} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Stack spacing={2}>
                        <Button variant="outlined" onClick={handleOnClose}>
                            取消
                        </Button>
                        <Button variant="contained" onClick={handleConfirmOnClick}>
                            送出
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
            <CancelAddDialog ref={cancelAddDialogRef} onClear={handleClearRowData} onClose={handleDialogMessageClose} />
        </React.Fragment>
    );
});

export default CheckinDialog;
